import React from 'react';
import { useShop } from '../../context/ShopContext';
import { formatINR } from '../../data/products';
import { 
  DollarSign, 
  ShoppingBag, 
  Sparkles, 
  TrendingUp, 
  Plus, 
  ShieldCheck, 
  Clock, 
  ArrowUpRight,
  Gem,
  CheckCircle2
} from 'lucide-react';

export default function AdminDashboard({ setActiveTab }) {
  const { products, orders, liveGoldRates } = useShop();

  const totalRevenue = orders.reduce((sum, ord) => sum + (ord.total || 0), 0);
  const pendingOrders = orders.filter((o) => o.status === 'Processing' || o.status === 'In Vault Transit').length;

  return (
    <div>
      {/* Top Banner */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(20, 20, 30, 0.9) 100%)',
        border: '1px solid var(--border-gold)',
        borderRadius: 'var(--radius-md)',
        padding: '1.5rem 2rem',
        marginBottom: '2rem',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem'
      }}>
        <div>
          <span className="badge badge-gold" style={{ marginBottom: '0.4rem' }}>
            <Sparkles size={12} /> Executive Control Room
          </span>
          <h2 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#fff' }}>
            M.L. Jewellers <span className="text-gold-gradient">& Kitchenware Command Center</span>
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            Real-time bullion market rates, customer orders, luxury jewellery vault and kitchenware inventory.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
          <button 
            className="btn btn-gold btn-sm"
            onClick={() => setActiveTab('products')}
          >
            <Plus size={16} /> Add Product
          </button>
          <button 
            className="btn btn-glass btn-sm"
            onClick={() => setActiveTab('orders')}
          >
            <ShoppingBag size={16} color="var(--gold-400)" /> Vault Orders
          </button>
          <button 
            className="btn btn-glass btn-sm"
            onClick={() => setActiveTab('gold-rates')}
          >
            <TrendingUp size={16} color="var(--gold-400)" /> Bullion Rates
          </button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '1.25rem',
        marginBottom: '2.5rem'
      }}>
        {/* Revenue */}
        <div style={{
          background: '#13131e',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          padding: '1.4rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.8rem' }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Total Order Value</span>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(212, 175, 55, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DollarSign size={18} color="var(--gold-400)" />
            </div>
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 800, color: '#fff', fontFamily: 'var(--font-serif)' }}>
            {formatINR(totalRevenue)}
          </div>
          <div style={{ fontSize: '0.74rem', color: '#34d399', marginTop: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
            <TrendingUp size={13} /> +18.4% this week
          </div>
        </div>

        {/* Total Orders */}
        <div style={{
          background: '#13131e',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          padding: '1.4rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.8rem' }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Total Orders</span>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(59, 130, 246, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShoppingBag size={18} color="#60a5fa" />
            </div>
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 800, color: '#fff' }}>
            {orders.length}
          </div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
            <strong style={{ color: '#f59e0b' }}>{pendingOrders} active</strong> in transit
          </div>
        </div>

        {/* Live 24K Gold Rate */}
        <div style={{
          background: '#13131e',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          padding: '1.4rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.8rem' }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Live 24K Pure Gold</span>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(16, 185, 129, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TrendingUp size={18} color="#34d399" />
            </div>
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--gold-400)', fontFamily: 'var(--font-serif)' }}>
            {formatINR(liveGoldRates.gold24K)}<span style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-muted)' }}>/g</span>
          </div>
          <div style={{ fontSize: '0.74rem', color: '#34d399', marginTop: '0.3rem' }}>
            Live Market: {liveGoldRates.change}
          </div>
        </div>

        {/* Active Inventory */}
        <div style={{
          background: '#13131e',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          padding: '1.4rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.8rem' }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Active Catalog</span>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(236, 72, 153, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Gem size={18} color="#f472b6" />
            </div>
          </div>
          <div style={{ fontSize: '1.75rem', fontWeight: 800, color: '#fff' }}>
            {products.length} Items
          </div>
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: '0.3rem' }}>
            100% BIS Assayed items
          </div>
        </div>
      </div>

      {/* Recent Orders Preview */}
      <div style={{
        background: '#13131e',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.25rem'
        }}>
          <div>
            <h3 style={{ fontSize: '1.15rem', color: '#fff', fontWeight: 700 }}>Recent Patron Orders</h3>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Latest insured deliveries & bookings</p>
          </div>
          <button 
            className="btn btn-glass btn-sm"
            onClick={() => setActiveTab('orders')}
          >
            View All Orders ({orders.length}) <ArrowUpRight size={14} />
          </button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.84rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                <th style={{ padding: '0.75rem 0.5rem' }}>Order ID</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Customer</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Jewellery Items</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Payment</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Amount</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.slice(0, 4).map((ord) => (
                <tr key={ord.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.04)' }}>
                  <td style={{ padding: '0.9rem 0.5rem', fontWeight: 700, color: 'var(--gold-400)' }}>
                    {ord.id}
                  </td>
                  <td style={{ padding: '0.9rem 0.5rem', color: '#fff', fontWeight: 600 }}>
                    {ord.customer?.name}
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{ord.customer?.phone}</div>
                  </td>
                  <td style={{ padding: '0.9rem 0.5rem', color: 'var(--text-secondary)', maxWidth: '220px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {ord.items?.map(i => i.name).join(', ')}
                  </td>
                  <td style={{ padding: '0.9rem 0.5rem', color: 'var(--text-muted)' }}>
                    {ord.paymentMethod}
                  </td>
                  <td style={{ padding: '0.9rem 0.5rem', fontWeight: 700, color: '#fff', fontFamily: 'var(--font-serif)' }}>
                    {formatINR(ord.total)}
                  </td>
                  <td style={{ padding: '0.9rem 0.5rem' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                      padding: '0.2rem 0.6rem',
                      borderRadius: '999px',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      background: ord.status === 'Delivered' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                      color: ord.status === 'Delivered' ? '#34d399' : '#fbbf24',
                      border: ord.status === 'Delivered' ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(245, 158, 11, 0.3)'
                    }}>
                      {ord.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Visual CMS & Site Management Hub */}
      <div style={{
        background: '#13131e',
        border: '1px solid var(--border-gold)',
        borderRadius: 'var(--radius-md)',
        padding: '1.8rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.2rem', flexWrap: 'wrap', gap: '0.8rem' }}>
          <div>
            <span className="badge badge-gold" style={{ marginBottom: '0.3rem' }}>
              <Sparkles size={12} /> Enterprise Storefront Suite
            </span>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff' }}>
              Visual Site CMS & Marketing Controls
            </h3>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Manage what visitors see live on the homepage without touching code.
            </p>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '1.2rem'
        }}>
          {/* Card 1 */}
          <div
            onClick={() => setActiveTab('cms-hero')}
            style={{
              background: '#0d0d16',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-sm)',
              padding: '1.2rem',
              cursor: 'pointer',
              transition: 'var(--transition)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--gold-400)'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
          >
            <div style={{ color: 'var(--gold-400)', fontWeight: 800, fontSize: '0.92rem', marginBottom: '0.3rem' }}>
              🖼️ Hero & Homepage Banner
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
              Edit main headline, royal badge, background image, and CTA buttons with live preview.
            </p>
          </div>

          {/* Card 2 */}
          <div
            onClick={() => setActiveTab('cms-announcements')}
            style={{
              background: '#0d0d16',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-sm)',
              padding: '1.2rem',
              cursor: 'pointer',
              transition: 'var(--transition)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--gold-400)'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
          >
            <div style={{ color: 'var(--gold-400)', fontWeight: 800, fontSize: '0.92rem', marginBottom: '0.3rem' }}>
              📢 Top Ticker & Announcements
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
              Broadcast festive discounts, shipping notices, and toggle live bullion rates.
            </p>
          </div>

          {/* Card 3 */}
          <div
            onClick={() => setActiveTab('cms-stories')}
            style={{
              background: '#0d0d16',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-sm)',
              padding: '1.2rem',
              cursor: 'pointer',
              transition: 'var(--transition)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--gold-400)'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
          >
            <div style={{ color: 'var(--gold-400)', fontWeight: 800, fontSize: '0.92rem', marginBottom: '0.3rem' }}>
              ⭕ Circular Story Highlights
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
              Manage Instagram-style circular reels icons with custom photo & category filter.
            </p>
          </div>

          {/* Card 4 */}
          <div
            onClick={() => setActiveTab('cms-testimonials')}
            style={{
              background: '#0d0d16',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-sm)',
              padding: '1.2rem',
              cursor: 'pointer',
              transition: 'var(--transition)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--gold-400)'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
          >
            <div style={{ color: 'var(--gold-400)', fontWeight: 800, fontSize: '0.92rem', marginBottom: '0.3rem' }}>
              ⭐ Reviews & Bride Stories
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
              Add real client reviews, 5-star ratings, photo avatars, and verified patron quotes.
            </p>
          </div>

          {/* Card 5 */}
          <div
            onClick={() => setActiveTab('cms-trust')}
            style={{
              background: '#0d0d16',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-sm)',
              padding: '1.2rem',
              cursor: 'pointer',
              transition: 'var(--transition)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--gold-400)'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
          >
            <div style={{ color: 'var(--gold-400)', fontWeight: 800, fontSize: '0.92rem', marginBottom: '0.3rem' }}>
              🛡️ Trust & Hallmark Guarantees
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
              Edit BIS 916 hallmarking terms, insured vault transit, and lifetime exchange policy.
            </p>
          </div>

          {/* Card 6 */}
          <div
            onClick={() => setActiveTab('cms-popup')}
            style={{
              background: '#0d0d16',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-sm)',
              padding: '1.2rem',
              cursor: 'pointer',
              transition: 'var(--transition)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--gold-400)'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
          >
            <div style={{ color: 'var(--gold-400)', fontWeight: 800, fontSize: '0.92rem', marginBottom: '0.3rem' }}>
              🎁 Festive Promo Offer Popup
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
              Turn on festive modal popup with countdown coupon code to multiply orders.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
